import React, { useEffect, Suspense, lazy } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import { Provider } from "react-redux";
import { landingStore } from "../../main/landing/landingStore";
import { runLandingPrefetchPipeline } from "../../main/landing/prefetchPipeline";
import ChunkErrorBoundary from "./ChunkErrorBoundary";
// Rendered once here, outside Switch/Suspense, so it never remounts on navigation — only the
// page content (and its footer, via LandingShell) goes blank while a chunk loads.
import LandingNav from "../../main/landing/LandingNav";
import { DashboardOpenProvider } from "../../main/landing/DashboardOpenOverlay";
// Home stays eager: it's the entry route and must paint with no extra chunk round-trip.
import HomePage from "../../main/HomePage";
// Everything else is code-split so a first visit to "/" doesn't download the whole marketing site.
const HowItWorksPage = lazy(() => import(/* webpackChunkName: "landing-hiw", webpackPrefetch: true */ "../../main/landing/HowItWorksPage"));
const ClaritaskPage = lazy(() => import(/* webpackChunkName: "landing-claritask", webpackPrefetch: true */ "../../main/landing/ClaritaskPage"));
const PricingPage = lazy(() => import(/* webpackChunkName: "landing-pricing" */ "../../main/landing/PricingPage"));
const ContactPage = lazy(() => import(/* webpackChunkName: "landing-contact" */ "../../main/landing/ContactPage"));
const Blog = lazy(() => import(/* webpackChunkName: "landing-blog" */ "../../main/Blog/Blog"));
const BlogDetails = lazy(() => import(/* webpackChunkName: "landing-blog" */ "../../main/Blog/Blog_details"));

const MARKETING_PATHS = ["/", "/how-it-works", "/claritask", "/pricing", "/contact", "/blog"];

function LandingRoutes() {
    useEffect(() => {
        runLandingPrefetchPipeline();
    }, []);

    return (
        <ChunkErrorBoundary>
            <DashboardOpenProvider>
                <div className="clk">
                    <LandingNav />
                    <Suspense fallback={null}>
                        <Switch>
                            <Route exact path="/" component={HomePage} />
                            <Route exact path="/how-it-works" component={HowItWorksPage} />
                            <Route exact path="/claritask" component={ClaritaskPage} />
                            <Route exact path="/pricing" component={PricingPage} />
                            <Route exact path="/contact" component={ContactPage} />
                            <Route exact path="/blog" component={Blog} />
                            <Route exact path="/blog/:slug" component={BlogDetails} />
                            <Route
                                path="*"
                                render={({ location }) => {
                                    const normalized = location.pathname.replace(/\/$/, "") || "/";
                                    const isBlogPath = normalized === "/blog" || normalized.startsWith("/blog/");
                                    if (!MARKETING_PATHS.includes(normalized) && !isBlogPath) {
                                        window.location.replace(
                                            location.pathname + location.search + location.hash
                                        );
                                        return null;
                                    }
                                    return <Redirect to="/" />;
                                }}
                            />
                        </Switch>
                    </Suspense>
                </div>
            </DashboardOpenProvider>
        </ChunkErrorBoundary>
    );
}

if (document.getElementById("app")) {
    ReactDOM.render(
        <Provider store={landingStore}>
            <BrowserRouter>
                <LandingRoutes />
            </BrowserRouter>
        </Provider>,
        document.getElementById("app")
    );
}
